import React from 'react';
import { Outlet } from 'react-router-dom';
import StudentSidebar from './StudentSidebar';
import AgentLexaWidget from '../Lexa/AgentLexaWidget';
import AvailabilityReminderModal from './AvailabilityReminderModal';
import { FeatureVisibilityProvider, useFeatureVisibility } from '../../context/FeatureVisibilityContext';

// Wraps every logged-in, non-admin route in App.js with the persistent
// left-side nav (docs/DECISIONS.md, "Persistent left-side student
// navigation + light-theme visual fix"). Pages that already render their
// own AppHeader/AppFooter (StudentProfile, ProgressTracker,
// PerformanceReports, QuizPortal, Assessments, the placeholder pages) need
// no internal changes - they just gain this as an outer column.
// Home.jsx/CurriculumMenuPage.jsx keep their own bespoke header/MegaMenu
// nested inside the content area, trimmed of the nav links this sidebar
// now owns.
//
// SettingsModalProvider/SettingsModal used to be mounted here (docs/
// DECISIONS.md, "Settings modal" decision - at the time, every place that
// could open Settings was a descendant of this layout). That stopped being
// true once AppHeader.jsx (which unconditionally calls useSettingsModal())
// started being reused by pages outside this layout - Registration.jsx
// first, then the Parent Login dashboard pages (docs/DECISIONS.md,
// 2026-08-25) - each of those crashed the whole app with "useSettingsModal
// must be used within SettingsModalProvider" (a real live bug, caught via
// a blank-screen report during Parent Login testing). Moved up to wrap the
// whole app in App.js instead, so it covers every page that can render
// AppHeader, not just this layout's subtree.
// FeatureVisibilityProvider (docs/DECISIONS.md, "Trial Plan: daily practice
// question quota" plan file, Part B) is mounted here, not inside
// StudentLayoutContent - this is the one place already wrapping every
// logged-in student route (StudentSidebar and AgentLexaWidget both need the
// same fetched data), and a provider can't be consumed by the component
// that renders it, hence the split.
export default function StudentLayout() {
  return (
    <FeatureVisibilityProvider>
      <StudentLayoutContent />
    </FeatureVisibilityProvider>
  );
}

function StudentLayoutContent() {
  const { isVisible } = useFeatureVisibility();

  return (
    <div className="flex h-screen overflow-hidden">
      <StudentSidebar />
      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
      {/* docs/DECISIONS.md, 2026-08-28 "LEXA - Phase 3" - global floating
          widget, present on every route this layout wraps. Fixed-position,
          doesn't participate in the flex layout above. Gated behind the
          "agent-lexa" feature (docs/DECISIONS.md, "Trial Plan: daily
          practice question quota" plan file, Part B) since 2026-08-30. */}
      {isVisible('agent-lexa') && <AgentLexaWidget />}
      <AvailabilityReminderModal />
    </div>
  );
}
